import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { RelayEnvironmentProvider } from 'react-relay';
import { cssMap } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Box } from '@atlaskit/primitives/compiled/box';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import {
	SideNav,
	SideNavFooter,
	SideNavPanelSplitter,
	useToggleSideNav,
} from '@atlaskit/navigation-system/layout/side-nav';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';

import { GlobalInviteWithLazyLoadedRelayProvider } from '@atlassian/global-invite/async';

import { PostOfficeConfluenceSideNavPlacement } from '@confluence/experiment-post-office-side-nav';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSettingsBackButtonUtils } from '@confluence/admin-side-navigation';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { AdminNavigationLoader } from '@confluence/side-navigation/entry-points/AdminNavigation.ts';
import { StarredSpacesList } from '@confluence/side-navigation/entry-points/StarredSpacesList.ts';
import { HubSettingsNavigation } from '@confluence/side-navigation/entry-points/HubSettingsNavigation.ts';
import { SpaceAdminNavigation } from '@confluence/side-navigation/entry-points/SpaceAdminNavigation.ts';
import { GlobalNavigation } from '@confluence/side-navigation/entry-points/GlobalNavigation.ts';
import {
	SpaceNavigation,
	SpaceNavigationLoadingSkeleton,
} from '@confluence/side-navigation/entry-points/SpaceNavigation.ts';
import {
	SpaceNavigationAffordanceDataProvider,
	useSpaceNavigationAffordanceData,
} from '@confluence/side-navigation/entry-points/SpaceNavigationAffordanceData.ts';
import { isDialogShown } from '@confluence/dialogs/entry-points/helpers.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	SIDE_NAVIGATION_SHORTCUT,
	UNIVERSAL_SIDE_NAVIGATION_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { WHITEBOARD_IFRAME_TITLE } from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useIsEditorOpen } from '@confluence/page-utils/entry-points/useIsEditorOpen.ts';
import { AnonymousAccessSideNavMessage } from '@confluence/anonymous-access/entry-points/AnonymousAccessSideNavMessage.ts';
import { useIsUserViewingSpaceAsAnon } from '@confluence/anonymous-access/entry-points/useIsUserViewingSpaceAsAnon.ts';
import { useIsUserViewingSpaceAsAnonFromFragment } from '@confluence/anonymous-access/entry-points/useIsUserViewingSpaceAsAnonFromFragment.ts';
import { usePersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/usePersistentInviteButtonEligible.ts';
import {
	type PersistentInviteLicenseFragmentRef,
	usePersistentInviteButtonEligibleFromFragment,
} from '@confluence/persistent-invite-button/entry-points/usePersistentInviteButtonEligibleFromFragment.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import {
	Nav4CollapseReminderSpotlightLoader,
	useHasDismissedNav4CollapseReminderSpotlight,
} from '@confluence/nav4-onboarding/entry-points/Nav4CollapseReminderSpotlightLoader.ts';
import { maybeShowDeprecatedShortcutFlag } from '@confluence/shortcuts/entry-points/DeprecatedShortcutFlag.tsx';
import { isBlogPage } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { useOmniBarVisibilityActions } from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';
import {
	useSideNavigationCollapsedActions,
	useSideNavigationCollapsedState,
} from '@confluence/page-layout-side-navigation-context/entry-points/SideNavigationCollapsedStore.ts';
import { PlatformNavigation } from '@confluence/side-navigation/entry-points/PlatformNavigation.ts';
import { ConnectorPromoSidebarEntry } from '@confluence/experiment-twcg-1489-cc-3p-connector-onboarding/entry-points/ConnectorPromoSidebarEntry.ts';

import { useSideNavInitialState } from './useSideNavInitialState.ts';
import { useScrollTree } from './useScrollTree.ts';
import {
	ADMIN_HOME_ID,
	type PRODUCT_HOME_ID,
	CONTAINER_ADMIN_HOME_ID,
	CONTAINER_HOME_ID,
	COMPANY_HUB_SETTINGS_ID,
	CONTAINER_HOME_LOADING_ID,
} from './paths.ts';
import { SideNavigationContent } from './SideNavigationContent.tsx';

export type SideNavView =
	| typeof CONTAINER_HOME_LOADING_ID
	| typeof CONTAINER_HOME_ID
	| typeof CONTAINER_ADMIN_HOME_ID
	| typeof COMPANY_HUB_SETTINGS_ID
	| typeof ADMIN_HOME_ID
	| typeof PRODUCT_HOME_ID;

const i18n = defineMessages({
	dragHandle: {
		id: 'page-layout.side-navigation.drag-handle',
		description:
			'A label to tell visually impaired users that the selected element is the side navigation drag handle for resizing the sidebar',
		defaultMessage: 'Side Navigation Drag Handle',
	},
	sideNavPanelSplitterTooltipContent: {
		id: 'page-layout.side-navigation.side-nav-panel-splitter-tooltip-content',
		description:
			'Text content of the tooltip that appears when hovering over the side nav panel splitter, telling users that double clicking on the element will collapse the side nav',
		defaultMessage: 'Double click to collapse',
	},
	globalInviteButtonLabel: {
		id: 'page-layout.side-navigation.global-invite-button-label',
		description: 'Text content of the button that triggers the modal dialog.',
		defaultMessage: 'Invite people',
	},
});

const resize = () => {
	window.dispatchEvent(new Event('resize'));
};

/**
 * Used when the built-in ADS side nav interactions are enabled.
 * Uses the `Ctrl+[` shortcut that is built into the ADS navigation system, instead of Confluence's
 * own `Ctrl+[` shortcut (UNIVERSAL_SIDE_NAVIGATION_SHORTCUT).
 * But, we keep Confluence's `[` shortcut (SIDE_NAVIGATION_SHORTCUT) on non-edit routes, and display
 * an educational flag pointing to the new shortcut.
 */
const ExpandKeyboardShortcutFHS = withFlags(
	({ flags, onToggleFromShortcut }: WithFlagsProps & { onToggleFromShortcut: () => void }) => {
		const isEditorOpen = useIsEditorOpen();
		const toggleSideNav = useToggleSideNav();

		const toggle = useCallback(() => {
			if (!isDialogShown()) {
				onToggleFromShortcut();
				toggleSideNav();

				// Show the educational flag for the `Ctrl+[` shortcut, when the `[` shortcut is used.
				maybeShowDeprecatedShortcutFlag({
					flags,
					acceleratorTriggered: SIDE_NAVIGATION_SHORTCUT,
					accelerator: UNIVERSAL_SIDE_NAVIGATION_SHORTCUT,
					deprecatedAccelerator: SIDE_NAVIGATION_SHORTCUT,
				});
			}
		}, [flags, onToggleFromShortcut, toggleSideNav]);

		if (isEditorOpen) {
			// Edit routes will still have the side nav shortcut that is built into the ADS SideNav component.
			return null;
		}

		// Non-edit routes still have the `[` shortcut, which will eventually be deprecated.
		// An educational flag is shown during the transition process.
		return <GeneralShortcutListener accelerator={SIDE_NAVIGATION_SHORTCUT} listener={toggle} />;
	},
);

const useIsModeGlobal = () => {
	const { match } = useContext(RoutesContext);

	const [spaceKey] = usePageSpaceKey();

	return match?.query?.mode === 'global' || isCompanyHubSpaceKey(spaceKey);
};

//todo: are all these views still relevant? I'm not certain they are
const getProductSideNavigation = (
	view: SideNavView | undefined,
	isSpaceSettingsScreen: boolean,
	isModeGlobal: boolean,
	isOnAnySettingsScreen: boolean,
	unhandledError: ApolloErrorInterop | undefined,
	isLicensed: boolean,
) => {
	// hide product nav if mode=global unless we're on a settings page
	if (isModeGlobal && !isOnAnySettingsScreen) {
		return null;
	}

	switch (view) {
		case CONTAINER_HOME_LOADING_ID:
			return <SpaceNavigationLoadingSkeleton />;
		case CONTAINER_ADMIN_HOME_ID:
			return <SpaceAdminNavigation />;
		case COMPANY_HUB_SETTINGS_ID:
			return <HubSettingsNavigation />;
		case ADMIN_HOME_ID:
			return <AdminNavigationLoader />;
		case CONTAINER_HOME_ID:
			return (
				<SpaceNavigation
					isSpaceSettingsScreen={isSpaceSettingsScreen}
					{...(unhandledError && { unhandledError })}
					isBlogNavigation={isBlogPage()}
				/>
			);
		default:
			return isLicensed ? <StarredSpacesList /> : null;
	}
};

type SideNavCallbacks = {
	onCollapse?: () => void;
	onExpand?: () => void;
	onPeekStart?: () => void;
	onPeekEnd?: () => void;
};

type PlatformNavProps = SideNavCallbacks & {
	view: SideNavView | undefined;
	isSpaceSettingsScreen: boolean;
};

const ConfluenceRelayEnvironmentBoundary = ({ children }: { children: React.ReactNode }) => {
	if (!fg('navx-confluence-product-nav-relay-boundary')) {
		return <>{children}</>;
	}

	return (
		<RelayEnvironmentProvider environment={getRelayEnvironment()}>
			{children}
		</RelayEnvironmentProvider>
	);
};

export type SideNavProps = PlatformNavProps & {
	setTriggeredByShortcut: (bool: boolean) => void;
	skiplinkText?: string;
	isNav4CollapseReminderSpotlightActive?: boolean;
	setIsNav4CollapseReminderSpotlightActive?: Dispatch<SetStateAction<boolean>>;
};

// Hoisted so `bottomElem` keeps a stable element identity across renders — GSN's `SideNav` memoises
// that prop by reference, and a fresh element re-renders the navigation tree (COPPER-1474).
const postOfficeSideNavPlacement = <PostOfficeConfluenceSideNavPlacement />;

type AnonymousAccessEligibility = ReturnType<typeof useIsUserViewingSpaceAsAnon>;

const PlatformNavigationInner = (props: PlatformNavProps): React.JSX.Element => {
	const [spaceKey] = usePageSpaceKey();
	const affordanceData = useSpaceNavigationAffordanceData();

	return affordanceData?.spaceFragmentRef ? (
		<RouteBackedPlatformNavigationInner
			{...props}
			spaceKey={spaceKey || ''}
			spaceFragmentRef={affordanceData.spaceFragmentRef}
		/>
	) : (
		<LegacyPlatformNavigationInner {...props} spaceKey={spaceKey || ''} />
	);
};

const RouteBackedPlatformNavigationInner = ({
	spaceKey,
	spaceFragmentRef,
	...props
}: PlatformNavProps & {
	spaceKey: string;
	spaceFragmentRef: NonNullable<
		ReturnType<typeof useSpaceNavigationAffordanceData>
	>['spaceFragmentRef'];
}): React.JSX.Element => {
	const anonymousAccessEligibility = useIsUserViewingSpaceAsAnonFromFragment({
		spaceKey,
		spaceFragmentRef,
	});

	return (
		<PlatformNavigationInnerLayout
			{...props}
			anonymousAccessEligibility={anonymousAccessEligibility}
		/>
	);
};

const LegacyPlatformNavigationInner = ({
	spaceKey,
	...props
}: PlatformNavProps & { spaceKey: string }): React.JSX.Element => {
	const anonymousAccessEligibility = useIsUserViewingSpaceAsAnon({ spaceKey });

	return (
		<PlatformNavigationInnerLayout
			{...props}
			anonymousAccessEligibility={anonymousAccessEligibility}
		/>
	);
};

const PlatformNavigationInnerLayout = ({
	view,
	isSpaceSettingsScreen,
	onCollapse,
	onExpand,
	onPeekStart,
	onPeekEnd,
	anonymousAccessEligibility,
}: PlatformNavProps & {
	anonymousAccessEligibility: AnonymousAccessEligibility;
}) => {
	const { scrollTree } = useScrollTree({
		view,
	});
	const isModeGlobal = useIsModeGlobal(); //instead of disabling the whole left nav, now mode=Global will disable only the space Navigation
	const { isLicensed } = useSessionData();
	const isPageWithinSpaceSettings = view === CONTAINER_ADMIN_HOME_ID;
	const isPageWithinSiteSettings = view === ADMIN_HOME_ID;
	const isHubSettingsScreen = view === COMPANY_HUB_SETTINGS_ID;
	const isOnAnySettingsScreen =
		isPageWithinSpaceSettings || isPageWithinSiteSettings || isHubSettingsScreen;
	const {
		isUserViewingSpaceAsAnon,
		loading: anonymousAccessQueryLoading,
		error: anonymousAccessQueryError,
	} = anonymousAccessEligibility;

	const productSideNavigation = useMemo(
		() =>
			getProductSideNavigation(
				view,
				isSpaceSettingsScreen,
				isModeGlobal,
				isOnAnySettingsScreen,
				anonymousAccessQueryError,
				isLicensed,
			),
		[
			view,
			isSpaceSettingsScreen,
			isModeGlobal,
			isOnAnySettingsScreen,
			anonymousAccessQueryError,
			isLicensed,
		],
	);

	// `content` becomes GSN's `postItemsElem`, which `SideNav` memoises by reference to build
	// `postMainElemForLayout`. Rebuilding it inline on every render busted that memo and cascaded a
	// re-render through PostMainItems and the space page tree on every route-state change
	// (COPPER-1474).
	const content = useMemo(
		() => (
			<>
				<ConfluenceRelayEnvironmentBoundary>
					{productSideNavigation}
				</ConfluenceRelayEnvironmentBoundary>
				<ConnectorPromoSidebarEntry isNav4 />
				{isUserViewingSpaceAsAnon ? (
					<AnonymousAccessSideNavMessage />
				) : (
					!anonymousAccessQueryLoading && !anonymousAccessQueryError && <SideNavFooterWrapped />
				)}
			</>
		),
		[
			productSideNavigation,
			isUserViewingSpaceAsAnon,
			anonymousAccessQueryLoading,
			anonymousAccessQueryError,
		],
	);

	return (
		//we will likely need callbacks for all expand and collapse events see useOmniBarVisibilityActions navx-3995
		<UFOSegment name="side-nav">
			<SideNavigationContent>
				<PlatformNavigation
					content={content}
					hideGSN={isOnAnySettingsScreen}
					scrollTreeToCurrent={scrollTree}
					onExpand={onExpand}
					onCollapse={onCollapse}
					onPeekStart={onPeekStart}
					onPeekEnd={onPeekEnd}
					bottomElem={
						fg('navx-global-side-nav-confluence') ? postOfficeSideNavPlacement : undefined
					}
				/>
			</SideNavigationContent>
		</UFOSegment>
	);
};

const SideNavigationInner = (props: SideNavProps): React.JSX.Element => {
	const [spaceKey] = usePageSpaceKey();
	const affordanceData = useSpaceNavigationAffordanceData();

	return affordanceData?.spaceFragmentRef ? (
		<RouteBackedSideNavigationInner
			{...props}
			spaceKey={spaceKey || ''}
			spaceFragmentRef={affordanceData.spaceFragmentRef}
		/>
	) : (
		<LegacySideNavigationInner {...props} spaceKey={spaceKey || ''} />
	);
};

const RouteBackedSideNavigationInner = ({
	spaceKey,
	spaceFragmentRef,
	...props
}: SideNavProps & {
	spaceKey: string;
	spaceFragmentRef: NonNullable<
		ReturnType<typeof useSpaceNavigationAffordanceData>
	>['spaceFragmentRef'];
}): React.JSX.Element => {
	const anonymousAccessEligibility = useIsUserViewingSpaceAsAnonFromFragment({
		spaceKey,
		spaceFragmentRef,
	});

	return (
		<SideNavigationInnerLayout {...props} anonymousAccessEligibility={anonymousAccessEligibility} />
	);
};

const LegacySideNavigationInner = ({
	spaceKey,
	...props
}: SideNavProps & { spaceKey: string }): React.JSX.Element => {
	const anonymousAccessEligibility = useIsUserViewingSpaceAsAnon({ spaceKey });

	return (
		<SideNavigationInnerLayout {...props} anonymousAccessEligibility={anonymousAccessEligibility} />
	);
};

const SideNavigationInnerLayout = ({
	view,
	isSpaceSettingsScreen,
	isNav4CollapseReminderSpotlightActive,
	setIsNav4CollapseReminderSpotlightActive,
	onCollapse,
	onExpand,
	onPeekStart,
	onPeekEnd,
	setTriggeredByShortcut,
	anonymousAccessEligibility,
}: SideNavProps & {
	anonymousAccessEligibility: AnonymousAccessEligibility;
}): React.JSX.Element => {
	const isFhsEnabled = useIsFhsEnabled();
	const intl = useIntl();
	const isModeGlobal = useIsModeGlobal(); //instead of disabling the whole left nav, now mode=Global will disable only the space Navigation
	const { initialCollapsedState, initialWidth, shouldForceExpand, needsPostHydrationCollapse } =
		useSideNavInitialState();

	const { saveCurrentRoute } = useSettingsBackButtonUtils();
	const { isLicensed } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isPageWithinSpaceSettings = view === CONTAINER_ADMIN_HOME_ID;
	const isPageWithinSiteSettings = view === ADMIN_HOME_ID;
	const isHubSettingsScreen = view === COMPANY_HUB_SETTINGS_ID;
	const isOnAnySettingsScreen =
		isPageWithinSpaceSettings || isPageWithinSiteSettings || isHubSettingsScreen;
	const toggleSideNav = useToggleSideNav();
	// Collapse only once: repeated toggles can prevent Suspense hydration updates from finishing.
	const hasAppliedPostHydrationCollapse = useRef(false);

	// After hydration completes, collapse the sidebar if the user's localStorage preference was collapsed
	useEffect(() => {
		if (needsPostHydrationCollapse && !hasAppliedPostHydrationCollapse.current) {
			hasAppliedPostHydrationCollapse.current = true;
			toggleSideNav();
		}
	}, [needsPostHydrationCollapse, toggleSideNav]);
	const pressTracing = usePressTracing('side-nav-collapse-reminder-spotlight');
	const {
		isUserViewingSpaceAsAnon,
		loading: anonymousAccessQueryLoading,
		error: anonymousAccessQueryError,
	} = anonymousAccessEligibility;
	const [{ isCollapsed }] = useSideNavigationCollapsedState();

	const productSideNavigation = getProductSideNavigation(
		view,
		isSpaceSettingsScreen,
		isModeGlobal,
		isOnAnySettingsScreen,
		anonymousAccessQueryError,
		isLicensed,
	);

	const fireSidebarResizeEndAnalytics = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'resized',
					actionSubject: 'spaceNavigation',
					source: 'containerNavigation',
					attributes: {
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
						initialWidth,
						finalWidth,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleToggleFromShortcut = useCallback(() => {
		setTriggeredByShortcut(true);
	}, [setTriggeredByShortcut]);

	// If current route is not a settings page, save the current route to session storage.
	// The saved route is read by the back button whenever the user lands in Site or Space settings in the same session.
	if (!isPageWithinSpaceSettings && !isPageWithinSiteSettings && !isHubSettingsScreen) {
		saveCurrentRoute();
	}

	const { isNav4CollapseReminderSpotlightDismissed } =
		useHasDismissedNav4CollapseReminderSpotlight();

	const canToggleWithShortcut = useCallback(() => {
		// Block the side nav toggle keyboard shortcut when a modal is open
		return !isDialogShown();
	}, []);

	return (
		<SideNav
			defaultWidth={initialWidth}
			defaultCollapsed={isFhsEnabled ? undefined : initialCollapsedState === 'collapsed'}
			onExpand={onExpand}
			onCollapse={onCollapse}
			onPeekStart={onPeekStart}
			onPeekEnd={onPeekEnd}
			id="side-navigation"
			canToggleWithShortcut={canToggleWithShortcut}
		>
			<UFOSegment name="side-nav">
				<SideNavigationContent>
					<ErrorBoundary
						attribution={Attribution.DISCO}
						attributes={{
							errorBoundaryId: 'sideNavigation-globalNavigation',
						}}
					>
						<GlobalNavigation
							productSideNavigation={productSideNavigation}
							isOnAnySettingsScreen={isOnAnySettingsScreen}
							isCollapsed={isCollapsed}
						/>
					</ErrorBoundary>
					{isNav4CollapseReminderSpotlightActive &&
						setIsNav4CollapseReminderSpotlightActive &&
						!fg('confluence_frontend_spotlight_ads_migration') && (
							<Nav4CollapseReminderSpotlightLoader
								setIsNav4CollapseReminderSpotlightActive={setIsNav4CollapseReminderSpotlightActive}
							/>
						)}
					{!shouldForceExpand && (
						<ExpandKeyboardShortcutFHS onToggleFromShortcut={handleToggleFromShortcut} />
					)}
					<SideNavPanelSplitter
						onResizeEnd={({ initialWidth, finalWidth }) => {
							handleResizeEnd({
								finalWidth,
								eligibleForCollapseReminderSpotlight:
									!isNav4CollapseReminderSpotlightActive &&
									!isNav4CollapseReminderSpotlightDismissed,
								setIsNav4CollapseReminderSpotlightActive,
								pressTracing,
							});

							fireSidebarResizeEndAnalytics({
								initialWidth,
								finalWidth,
							});
						}}
						onResizeStart={onResizeStart}
						label={intl.formatMessage(i18n.dragHandle)}
						testId="side-navigation-panel-splitter"
						tooltipContent={intl.formatMessage(i18n.sideNavPanelSplitterTooltipContent)}
					/>
				</SideNavigationContent>
				{isUserViewingSpaceAsAnon ? (
					<AnonymousAccessSideNavMessage />
				) : (
					!anonymousAccessQueryLoading && !anonymousAccessQueryError && <SideNavFooterWrapped />
				)}
			</UFOSegment>
		</SideNav>
	);
};

const SideNavigationSkeleton = () => {
	const isFhsEnabled = useIsFhsEnabled();

	const { initialCollapsedState, initialWidth } = useSideNavInitialState();

	return (
		<SideNav
			defaultWidth={initialWidth}
			defaultCollapsed={isFhsEnabled ? undefined : initialCollapsedState === 'collapsed'}
			id="side-navigation-skeleton"
		>
			<UFOSegment name="side-nav-skeleton">
				<SideNavigationContent>
					<SpaceNavigationLoadingSkeleton />
				</SideNavigationContent>
			</UFOSegment>
		</SideNav>
	);
};

export const SideNavigation = (props: SideNavProps): React.JSX.Element => {
	const { setIsCollapsed } = useSideNavigationCollapsedActions();
	const [triggeredByShortcut, setTriggeredByShortcut] = useState(false);
	const { scrollTree } = useScrollTree({
		view: props.view,
	});
	const isOnEditRoute = useIsEditorPage();
	const [spaceKey] = usePageSpaceKey();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireSidebarToggleAnalytics = useCallback(
		(action: 'collapsed' | 'expanded', triggeredBy: 'keyboardShortcut' | 'click') => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject: 'spaceNavigation',
					source: 'containerNavigation',
					attributes: {
						isOnEditRoute,
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
						triggeredBy,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute],
	);

	const onCollapse = useCallback(() => {
		// Track the collapsed state in both the legacy and GSN paths. @atlaskit/navigation-system exposes
		// no public read hook for side nav visibility (useSideNavVisibility is documented as
		// package-internal), so this store is the only way Confluence product code can read it.
		setIsCollapsed(true);
		// Mirror the collapsed state to the legacy PERSISTED_LEFT_SIDEBAR_COLLAPSED localStorage key in
		// both the legacy and GSN paths. GlobalSideNavigation (navx-global-side-nav-confluence) persists
		// its collapse state to network storage only, but the Suspense fallback skeleton
		// (SideNavigationSkeleton) and SSR both read this key via useSideNavInitialState. Without the
		// mirror the skeleton/SSR render a stale state and the nav — and the ADS toggle icon — visibly
		// snap (and hydration-mismatch) once the real nav loads. This is the collapse-state counterpart
		// to the PERSISTED_LEFT_SIDEBAR_WIDTH mirror added in #395995.
		confluenceLocalStorageInstance.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
			'collapsed',
		);
		resize();
		fireSidebarToggleAnalytics('collapsed', triggeredByShortcut ? 'keyboardShortcut' : 'click');
		setTriggeredByShortcut(false);
	}, [fireSidebarToggleAnalytics, triggeredByShortcut, setIsCollapsed]);

	const onExpand = useCallback(() => {
		// See the onCollapse comment above for why this is tracked in both paths.
		setIsCollapsed(false);
		// Mirror the expanded state to localStorage in both paths so the skeleton/SSR stay in sync with
		// GSN's network-persisted state. See the onCollapse comment above for the full rationale.
		confluenceLocalStorageInstance.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
			'expanded',
		);
		scrollTree();
		resize();
		fireSidebarToggleAnalytics('expanded', triggeredByShortcut ? 'keyboardShortcut' : 'click');
		setTriggeredByShortcut(false);
	}, [scrollTree, fireSidebarToggleAnalytics, triggeredByShortcut, setIsCollapsed]);

	const { requestHideOmniBar, releaseHideOmniBar } = useOmniBarVisibilityActions();

	const onPeekStart = useCallback(() => {
		requestHideOmniBar();
	}, [requestHideOmniBar]);

	const onPeekEnd = useCallback(() => {
		releaseHideOmniBar();
	}, [releaseHideOmniBar]);

	// Release hide request on unmount so we don't leave omnibar stuck hidden
	// (e.g. user navigates away while sidebar flyout is open).
	useEffect(() => {
		return () => {
			releaseHideOmniBar();
		};
	}, [releaseHideOmniBar]);

	const withAffordanceData = (sideNavigation: React.JSX.Element) =>
		spaceKey &&
		!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_affordances_frag_reader') &&
		expVal('confluence_exp_space_nav_contract', 'isEnabled', false) ? (
			<SpaceNavigationAffordanceDataProvider spaceKey={spaceKey}>
				{sideNavigation}
			</SpaceNavigationAffordanceDataProvider>
		) : (
			sideNavigation
		);

	return fg('navx-global-side-nav-confluence') ? (
		<SuspenseWithDataFetcher
			dataFetcherName="side-navigation"
			fallback={<SideNavigationSkeleton />}
		>
			{withAffordanceData(
				<PlatformNavigationInner
					view={props.view}
					isSpaceSettingsScreen={props.isSpaceSettingsScreen}
					onCollapse={onCollapse}
					onExpand={onExpand}
					onPeekStart={onPeekStart}
					onPeekEnd={onPeekEnd}
				/>,
			)}
		</SuspenseWithDataFetcher>
	) : (
		<SuspenseWithDataFetcher
			dataFetcherName="side-navigation"
			fallback={<SideNavigationSkeleton />}
		>
			{withAffordanceData(
				<SideNavigationInner
					{...props}
					onCollapse={onCollapse}
					onExpand={onExpand}
					onPeekStart={onPeekStart}
					onPeekEnd={onPeekEnd}
					setTriggeredByShortcut={setTriggeredByShortcut}
				/>,
			)}
		</SuspenseWithDataFetcher>
	);
};

const getSideNavContent = () => {
	// Because we render the PostOfficeConfluenceSideNavPlacement in the bottomElem of the PlatformNavigation when navx-global-side-nav-confluence is enabled, we don't want to render it twice in the SideNavFooter.
	return !fg('navx-global-side-nav-confluence') && <PostOfficeConfluenceSideNavPlacement />;
};

const SideNavFooterWrapped = () => {
	return (
		<>
			{getSideNavContent()}
			<SideNavFooterContent />
		</>
	);
};

const globalInviteButtonStyles = cssMap({
	root: {
		flexGrow: 1,
	},
});

const Nav4SideNavFooterContent = withFlags(({ flags }: WithFlagsProps) => {
	const intl = useIntl();

	return (
		<Inline space="space.100">
			<Box xcss={globalInviteButtonStyles.root}>
				<GlobalInviteWithLazyLoadedRelayProvider product="confluence" showFlag={flags.showFlag}>
					{intl.formatMessage(i18n.globalInviteButtonLabel)}
				</GlobalInviteWithLazyLoadedRelayProvider>
			</Box>
		</Inline>
	);
});

const SideNavFooterContent = () => {
	const affordanceData = useSpaceNavigationAffordanceData();

	return affordanceData?.licenseFragmentRef ? (
		<RouteBackedSideNavFooterContent licenseFragmentRef={affordanceData.licenseFragmentRef} />
	) : (
		<LegacySideNavFooterContent />
	);
};

const RouteBackedSideNavFooterContent = ({
	licenseFragmentRef,
}: {
	licenseFragmentRef: PersistentInviteLicenseFragmentRef;
}) => {
	const eligibility = usePersistentInviteButtonEligibleFromFragment({ licenseFragmentRef });

	return <SideNavFooterContentLayout eligibility={eligibility} />;
};

const LegacySideNavFooterContent = () => {
	const eligibility = usePersistentInviteButtonEligible();

	return <SideNavFooterContentLayout eligibility={eligibility} />;
};

const SideNavFooterContentLayout = ({
	eligibility: { error, loading, isEligible },
}: {
	eligibility: ReturnType<typeof usePersistentInviteButtonEligible>;
}) => {
	if (loading) return null;

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		} else {
			return <ErrorDisplay error={error} />;
		}
	}

	return (
		isEligible && (
			<SideNavFooter>
				<TransparentErrorBoundary attribution={Attribution.NAVX}>
					<Nav4SideNavFooterContent />
				</TransparentErrorBoundary>
			</SideNavFooter>
		)
	);
};

// CCT-951 TEMPORARY WORKAROUND to allow resizing of side-nav when viewing a Whiteboard (rendered in an iFrame)
// Can be removed when DSP-5931 is fixed and @atlaskit/page-layout is bumped
const onResizeStart = () => {
	if (typeof document !== 'undefined') {
		(
			document.querySelector(`iframe[title="${WHITEBOARD_IFRAME_TITLE}"]`) as HTMLElement
		)?.style.setProperty('pointer-events', 'none');
	}
};

const handleResizeEnd = ({
	finalWidth,
	eligibleForCollapseReminderSpotlight,
	setIsNav4CollapseReminderSpotlightActive,
	pressTracing,
}: {
	finalWidth: number;
	eligibleForCollapseReminderSpotlight: boolean;
	setIsNav4CollapseReminderSpotlightActive?: Dispatch<SetStateAction<boolean>>;
	pressTracing: () => void;
}) => {
	// CCT-951 TEMPORARY WORKAROUND to allow resizing of side-nav when viewing a Whiteboard (rendered in an iFrame)
	// Can be removed when DSP-5931 is fixed and @atlaskit/page-layout is bumped
	if (typeof document !== 'undefined') {
		(
			document.querySelector(`iframe[title="${WHITEBOARD_IFRAME_TITLE}"]`) as HTMLElement
		)?.style.removeProperty('pointer-events');
	}

	confluenceLocalStorageInstance.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_WIDTH,
		finalWidth,
	);

	if (eligibleForCollapseReminderSpotlight) {
		pressTracing();
		setIsNav4CollapseReminderSpotlightActive?.(true);
	}
};
